import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
    Router,
    RouterModule
} from '@angular/router';
import { concatMap, filter, switchMap, takeUntil } from 'rxjs/operators';
import { Permissions } from './Models/Permissions';
import { Tenant } from './Models/tenant';
import { TenantProperty } from './Models/tenantProperty';
import { PermissionService } from './Services/permission.service';
import { TenantService } from './Services/tenant.service';
import { PendoService } from './Services/pendo.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ToastComponent } from '@app/Common';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less', './aw-controls.less'],
    providers: [Title],
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatSidenavModule,
        MatToolbarModule,
        MatListModule,
        MatSelectModule,
        MatOptionModule,
        ToastComponent,
    ]
})
export class AppComponent implements OnInit, OnDestroy {
  private titleService: Title;
  private tenantType: string;
  private tenantPermissionGroup: string;
  private errorMessage: string;
  private readonly destroy$ = new Subject<void>();
  // Set once and never reset: logout flows reload the page, reinitialising this component.
  private subscriptionsSetUp = false;
 
  readonly permissions = Permissions;

  filteredTenants: Tenant[] = [];
  selectedTenant: Tenant;

  tenantTypeDisplayName: string;
  tenantStatus: string;

  get selectedTenantHasAirswebApplication(): boolean {
    return this.tenantService.selectedTenantHasAirswebApplication;
  }

  constructor(
    public tenantService: TenantService,
    private readonly router: Router,
    private readonly title: Title,
    private readonly permissionService: PermissionService,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly pendoService: PendoService
  ) {
    this.titleService = this.title;
  }

  loadTenants(): void {
    this.tenantService.getTenants();
  }

  ngOnInit(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setupTenantSubscriptions();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelect(): void {
    if (!this.selectedTenant.HasAccess) {
      this.selectedTenant = null;
      return;
    }

    this.router.navigate([`/tenant/${this.selectedTenant.TenantId}/`]);
  }
  
  onDropdownClosed(filterInput: HTMLInputElement): void {
    filterInput.value = '';
    this.filteredTenants = this.tenantService.tenants;
  }

  filterTenants(query: string): void {
    const allTenants = this.tenantService.tenants;
    if(query.length === 0) {
      this.filteredTenants = allTenants;
      return;
    }
    this.filteredTenants = allTenants.filter((tenant) => {
        return tenant.Text?.toLowerCase().includes(query.toLowerCase())
            || tenant.Value?.toLowerCase().includes(query.toLowerCase())
            || tenant.CrmName?.toLowerCase().includes(query.toLowerCase());
    });
  }

  hasPermission(permission: string): boolean {
      return this.permissionService.hasPermission(permission);
  }

  accessAva(): void {
    this.tenantService.access(this.tenantService.selectedTenant)
        .subscribe(
            avaUrl => { window.open(avaUrl.toString()); },
            err => { this.errorMessage = err;}
        );
}

  private getTenantTypeDisplayName(tenantTypes: TenantProperty[]): string {
    const matchingType = tenantTypes.find(
      (type) => type.Value === this.tenantType
    );
    return matchingType ? matchingType.Text : '';
  }

  private setupTenantSubscriptions(): void {
    if (this.subscriptionsSetUp) {
      return;
    }
    this.subscriptionsSetUp = true;

    this.pendoService.initPendoForUser();

    this.loadTenants();

    this.tenantService.tenants$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenants) => (this.filteredTenants = tenants));

    // Update component display state on tenant selection
    this.tenantService.selectedTenant$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant) => {
        this.selectedTenant = tenant;
        if (tenant) {
          this.titleService.setTitle(`ACT - ${tenant.Value}`);
        }
      });

    // Identify the selected tenant in Pendo; switchMap cancels stale in-flight calls
    this.tenantService.selectedTenant$
      .pipe(
        filter((tenant): tenant is Tenant => tenant !== null),
        switchMap(tenant => this.pendoService.identifyTenant(tenant)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.tenantService
      .currentTenantStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => (this.tenantStatus = result));

    this.tenantService
      .currentTenantType()
      .pipe(
        concatMap((tenantType: string) => {
          this.tenantType = tenantType;
          return this.tenantService.getTenantTypes();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.tenantTypeDisplayName = this.getTenantTypeDisplayName(result);
      });

    this.tenantService
      .currentTenantPermissionGroup()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => (this.tenantPermissionGroup = result));
  }
}
